import { SpanLoading } from '../fetch-status/span-loading';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { InteractCloseIcon } from '@/client/assets';
import { IS_DEBUG } from '@/client/confs/app';
import { useCheckUserInfo } from '@/client/hooks/use-check-user-info';
import { useInviteCodeStore } from '@/client/store/invite-code';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { refetchSignedUserInfo } from '@/client/utils/query-status';
import { inviteCodeSchema, usernameSchema } from '@/client/utils/schema';
import { HTMLAttributes, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type SignNameProps = HTMLAttributes<HTMLDivElement> & {
  onClose?: () => void;
};

export const SignInfo = ({ onClose, className, ...props }: SignNameProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>('');
  const inviteCode = useInviteCodeStore(state => state.inviteCode);
  const setInviteCode = useInviteCodeStore(state => state.setInviteCode);
  const [isLoading, setIsLoading] = useState(false);
  const { data, isFetching: isCheckFetching } = useCheckUserInfo(api, name, inviteCode);
  const { nameInfo, codeInfo } = data ?? {};
  const hasUserName = useMemo(
    () => nameInfo?.name && Boolean(name) && nameInfo?.name?.trim() === name?.trim(),
    [name, nameInfo?.name]
  );
  const hasInviteCode = useMemo(
    () => codeInfo?.inviteCode && Boolean(inviteCode) && codeInfo?.inviteCode === inviteCode,
    [inviteCode, codeInfo?.inviteCode]
  );
  const isName = useMemo(() => !hasUserName && usernameSchema.safeParse(name).success, [hasUserName, name]);
  const isInviteCode = useMemo(
    () => hasInviteCode && inviteCodeSchema.safeParse(inviteCode).success,
    [hasInviteCode, inviteCode]
  );
  const handleSetName = async () => {
    const resultName = usernameSchema.safeParse(name);
    const resultInviteCode = inviteCodeSchema.safeParse(inviteCode);
    if (!resultName.success && !resultInviteCode.success) return;
    setIsLoading(true);
    try {
      await api.user.setNameAndInviteCode({ name: resultName.data, inviteByCode: resultInviteCode.data });
      refetchSignedUserInfo().catch(() => {});
      toast.success(t('set-info-success'));
      onClose?.();
    } catch (error) {
      toast.error(t('set-name-failed'));
      if (IS_DEBUG) console.log('Set name error: ', error);
    }
    setIsLoading(false);
  };
  return (
    <div {...props} className={cn('px-4 pb-4 md:p-4 rounded-6 space-y-8', className)}>
      <div className="flex items-center justify-between">
        <dl className="space-y-1">
          <dt className="text-5 leading-6 font-semibold text-foreground">{t('choose-a-name')}</dt>
          <dd className="font-medium text-xs leading-none text-foreground/40">{t('you-can-update-this-later')}</dd>
        </dl>
        <Button
          size="icon"
          variant="ghost"
          className="transition-all duration-200 hover:scale-90 focus:scale-80"
          onClick={onClose}
        >
          <InteractCloseIcon className="!size-6 fill-foreground/40" />
        </Button>
      </div>
      <div className="pos-relative">
        <Input
          type="text"
          placeholder={t('name')}
          className={cn(
            'h-13 text-base border border-foreground/4 shadow-sm focus-visible:ring-foreground/36',
            name && !isName && 'border-destructive ring-destructive'
          )}
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <div className="pos-absolute top-4 end-4">
          {name && isCheckFetching && <SpanLoading />}
          {!isCheckFetching && name && !hasUserName && (
            <div className="text-center justify-start text-beneficial/70 text-sm font-medium leading-tight">
              {t('available')}
            </div>
          )}
          {!isCheckFetching && name && hasUserName && (
            <div className="text-center justify-start text-destructive/70 text-sm font-medium leading-tight">
              {t('username-taken')}
            </div>
          )}
        </div>
      </div>
      <div className="pos-relative">
        <Input
          type="text"
          value={inviteCode}
          placeholder={t('invite-code-option')}
          className={cn(
            'h-13 text-base border border-foreground/4 shadow-sm focus-visible:ring-foreground/36',
            inviteCode && !isInviteCode && 'border-destructive ring-destructive'
          )}
          onChange={e => setInviteCode(e.target.value?.toUpperCase())}
        />
        <div className="pos-absolute top-4 end-4">
          {inviteCode && isCheckFetching && <SpanLoading />}
          {!isCheckFetching && inviteCode && hasInviteCode && (
            <div className="text-center justify-start text-color-[#28C04E]-700 text-sm font-medium leading-tight">
              {t('available')}
            </div>
          )}
          {!isCheckFetching && inviteCode && !hasInviteCode && (
            <div className="text-center justify-start text-color-[#CF2A2A]-700 text-sm font-medium leading-tight">
              {t('code-invalid')}
            </div>
          )}
        </div>
      </div>
      <div className="w-full text-center">
        <Button
          className="w-full h-14"
          disabled={
            isLoading || (Boolean(name) && !isName) || (Boolean(inviteCode) && !isInviteCode) || (!name && !inviteCode)
          }
          onClick={handleSetName}
        >
          {isLoading && <SpanLoading />}
          {t('start-prediction-journey')}
        </Button>
        <Button disabled={isLoading} onClick={onClose} variant="ghost" className="text-xs text-foreground/40">
          {t('skip')}
        </Button>
      </div>
    </div>
  );
};
